import { type FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Image from '@segment/matcha/components/Image'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import dataPrivacyPng2x from './assets/DataPrivacy@2x.png'
import dataPrivacyWebp from './assets/DataPrivacy.webp'
import dataPrivacyWebp2x from './assets/DataPrivacy@2x.webp'

const copy = {
  heading: `Data privacy, compliance, & governance`,
  description: `Implement a data strategy that is ethical, consent-based, and adheres to compliance standards.`,
}

const data = [
  {
    heading: `Minimize regulatory and reputation risks`,
    description: `Activate only consented first-party data that respects customer privacy and ensures end-to-end compliance with both regional and industry-specific regulations.`,
  },
  {
    heading: `Detect, classify, and block personally identifiable information`,
    description: `Use Segment’s Privacy Portal to define a centralized data inventory to prevent the collection and delivery of sensitive data. Give your customers a voice with our open source consent manager.`,
  },
  {
    heading: `Reduce deletion and suppression request SLAs`,
    description: `Segment’s Deletion and Suppression tooling streamlines removing and blocking data for specific users. Plus, you can even forward deletion requests to connected destinations.`,
  },
]

const button = {
  href: `/trust-center/`,
  text: `Segment Trust Center`,
}

const DataPrivacy: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper id="data-privacy">
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading variant="h2" tag="h2">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description variant="pLarge" tag="p">
              {copy.description}
            </CopyText.Description>
          </CopyTextBase>
        </Section.Copy>

        <Section.Contents>
          <ColumnsWrapper>
            <ColumnTrack columns={1} overrides={{ md: 2 }}>
              <Content>
                {data.map(({ heading, description }) => (
                  <FeatureBoxBase key={heading} alignment="left">
                    <FeatureBox.Body>
                      <FeatureBox.Title>{heading}</FeatureBox.Title>
                      <FeatureBox.Text variant="pMedium">{description}</FeatureBox.Text>
                    </FeatureBox.Body>
                  </FeatureBoxBase>
                ))}

                <ButtonWrapper>
                  <ButtonText
                    variant="primary"
                    size="medium"
                    href={getAbsoluteUrl(`${button.href}`, pageConfig)}
                    iconEnd={ChevronRightIcon}
                  >
                    {button.text}
                  </ButtonText>
                </ButtonWrapper>
              </Content>

              <div>
                <picture>
                  <source type="image/webp" srcSet={`${dataPrivacyWebp}, ${dataPrivacyWebp2x} 2x`} />
                  <StyledImage
                    width="1248"
                    height="1254"
                    src={dataPrivacyPng2x}
                    alt="Illustration: Data Privacy"
                    loading="lazy"
                  />
                </picture>
              </div>
            </ColumnTrack>
          </ColumnsWrapper>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    gap: 0;
  }
`

const ColumnsWrapper = styled.div`
  margin-top: 30px;
`

const Content = styled.div`
  max-width: 480px;
  display: grid;
  align-content: center;
  gap: 24px;
  justify-self: center;
`

const StyledImage = styled(Image)`
  width: 100%;
  max-width: 600px;
`
const ButtonWrapper = styled.div``

export default DataPrivacy
